import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter, makeGroupId } from "@/domains/library"
import { LockTransferredOutEvent } from "@/domains/lock/events"
import { transferLock } from "@/domains/on-chain"

interface TransferLockInteractionParams {
  account?: Address
  chainId: number
  config: Config
  vestedAddress: Address
  tokenId: number
  toAccount: Address
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const transferLockInteraction = async ({
  account,
  chainId,
  config,
  vestedAddress,
  tokenId,
  toAccount,
  emitter,
  eventBus,
}: TransferLockInteractionParams) => {
  const groupId = makeGroupId("transfer-lock")

  if (!account) {
    return emitter.emit("transfer-lock", {
      title: "Not connected",
      description: `You're not connected`,
      variant: "error",
    })
  }
  const result = await transferLock({
    chainId,
    config,
    vestedAddress,
    account,
    toAccount,
    tokenId,
  })

  if (result.success) {
    eventBus.publish(new LockTransferredOutEvent(groupId, tokenId, account, toAccount, result.tx))
    emitter.emit("transfer-lock", {
      title: "Transfer lock",
      description: `Transferred lock #${tokenId} to ${toAccount}`,
      variant: "success",
    })
  }

  if (result.success === false) {
    const errorMessage: string = result.error as string
    if (/attached/.exec(errorMessage)) {
      emitter.emit("error-transfer-lock", {
        title: "Transfer lock failed",
        description: `Unable to transfer lock, the lock is attached to a gauge and must be reset first.`,
        errorCode: "attached-scale-v1-liquidity",
        variant: "error",
      })

      return result
    }

    emitter.emit("error-transfer-lock", {
      title: "Transfer lock failed",
      description: `Unable to transfer lock`,
      variant: "error",
    })
  }

  return result
}
